"use client"

import { useEffect, useState } from "react"
import styles from "./PWAInstallPrompt.module.css"
import logo from "../../assets/images/logo.svg"

const PWAInstallPrompt = ({ onPromptVisibilityChange }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [showInstallOverlay, setShowInstallOverlay] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [currentBenefit, setCurrentBenefit] = useState(0)

  const isPWAInstalled = () => {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      ("standalone" in window.navigator && window.navigator.standalone)
    )
  }

  const benefits = [
    "3x Faster Course Loading",
    "100% Offline Access",
    "Zero Distractions",
    "Instant Code Compilation"
  ]

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
      if (!isPWAInstalled()) {
        setShowInstallOverlay(true)
        onPromptVisibilityChange(true)
        setTimeout(() => setIsAnimating(true), 100)
      }
    }

    const handleAppInstalled = () => {
      console.log("PWA was successfully installed!")
      setShowInstallOverlay(false)
      setDeferredPrompt(null)
      onPromptVisibilityChange(false)
      setIsAnimating(false)
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
    window.addEventListener("appinstalled", handleAppInstalled)

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
      window.removeEventListener("appinstalled", handleAppInstalled)
    }
  }, [onPromptVisibilityChange])

  // Rotate benefits text
  useEffect(() => {
    if (showInstallOverlay) {
      const interval = setInterval(() => {
        setCurrentBenefit((prev) => (prev + 1) % benefits.length)
      }, 2000)
      return () => clearInterval(interval)
    }
  }, [showInstallOverlay, benefits.length])

  const handleInstallClick = async () => {
    setIsAnimating(false)

    setTimeout(() => {
      setShowInstallOverlay(false)
      onPromptVisibilityChange(false)
    }, 300)

    if (deferredPrompt) {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      console.log(`User response to the install prompt: ${outcome}`)
      setDeferredPrompt(null)
    } else {
      console.warn("No install prompt available or already used.")
      if (navigator.userAgent.match(/iPad|iPhone|iPod/)) {
        console.log("On iOS, use the Share button and 'Add to Home Screen' manually.")
      }
    }
  }

  if (!showInstallOverlay) {
    return null
  }

  return (
    <div className={`${styles.overlay} ${isAnimating ? styles.overlayVisible : ""}`}>
      <div className={`${styles.overlayContent} ${isAnimating ? styles.contentVisible : ""}`}>
        {/* Floating Learning Elements */}
        <div className={styles.floatingElements}>
          <div className={styles.element1}>💡</div>
          <div className={styles.element2}>🚀</div>
          <div className={styles.element3}>⚡</div>
          <div className={styles.element4}>🎯</div>
          <div className={styles.element5}>📊</div>
        </div>

        {/* Header with Logo */}
        <div className={styles.header}>
          <div className={styles.logoContainer}>
            <img src={logo || "/placeholder.svg"} alt="inelf.com" className={styles.logo} />
            <div className={styles.logoGlow}></div>
          </div>
          <div className={styles.badge}>
            <span className={styles.badgeText}>Enhanced Experience</span>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.mainContent}>
          <h1 className={styles.mainTitle}>
            Supercharge Your
            <span className={styles.highlightText}> Learning Journey</span>
          </h1>
          
          <div className={styles.rotatingBenefit}>
            <span className={styles.benefitText}>{benefits[currentBenefit]}</span>
          </div>

          <p className={styles.description}>
            Join <strong>50,000+ learners</strong> who've upgraded to our lightning-fast app. 
            Get instant access to courses, compilers, and career resources with zero loading delays.
          </p>
        </div>

        {/* Feature Grid */}
        <div className={styles.featureGrid}>
          <div className={styles.feature}>
            <div className={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor" />
              </svg>
            </div>
            <div className={styles.featureContent}>
              <h3>Instant Loading</h3>
              <p>Courses load 3x faster than web version</p>
            </div>
          </div>

          <div className={styles.feature}>
            <div className={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={styles.featureContent}>
              <h3>Offline Learning</h3>
              <p>Download courses for offline study</p>
            </div>
          </div>

          <div className={styles.feature}>
            <div className={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={styles.featureContent}>
              <h3>Smart Compiler</h3>
              <p>Run code instantly without delays</p>
            </div>
          </div>

          <div className={styles.feature}>
            <div className={styles.featureIcon}>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <div className={styles.featureContent}>
              <h3>Progress Sync</h3>
              <p>Never lose your learning progress</p>
            </div>
          </div>
        </div>

        {/* Social Proof */}
        <div className={styles.socialProof}>
          <div className={styles.userAvatars}>
            <div className={styles.avatar}>👨‍💻</div>
            <div className={styles.avatar}>👩‍🎓</div>
            <div className={styles.avatar}>👨‍🎓</div>
            <div className={styles.avatar}>👩‍💻</div>
          </div>
          <div className={styles.proofText}>
            <strong>50,000+ students</strong> are already learning faster with our app
          </div>
        </div>

        {/* Performance Stats */}
        <div className={styles.statsContainer}>
          <div className={styles.stat}>
            <div className={styles.statNumber}>3x</div>
            <div className={styles.statLabel}>Faster</div>
          </div>
          <div className={styles.statDivider}></div>
          <div className={styles.stat}>
            <div className={styles.statNumber}>100%</div>
            <div className={styles.statLabel}>Ad-Free</div>
          </div>
          <div className={styles.statDivider}></div>
          <div className={styles.stat}>
            <div className={styles.statNumber}>0ms</div>
            <div className={styles.statLabel}>Delays</div>
          </div>
        </div>

        {/* Call to Action */}
        <div className={styles.ctaSection}>
          <button className={styles.installButton} onClick={handleInstallClick}>
            <div className={styles.buttonContent}>
              <div className={styles.buttonIcon}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={styles.buttonText}>
                <span className={styles.buttonMainText}>Download inelf App</span>
                <span className={styles.buttonSubText}>Free • Instant • No Registration</span>
              </div>
            </div>
            <div className={styles.buttonShine}></div>
            <div className={styles.buttonPulse}></div>
          </button>
          
          <div className={styles.trustIndicators}>
            <div className={styles.trustItem}>
              <span className={styles.trustIcon}>🔒</span>
              <span>Secure & Private</span>
            </div>
            <div className={styles.trustItem}>
              <span className={styles.trustIcon}>⚡</span>
              <span>Instant Install</span>
            </div>
            <div className={styles.trustItem}>
              <span className={styles.trustIcon}>📱</span>
              <span>Works Offline</span>
            </div>
          </div>
        </div>

        {/* Decorative Background Elements */}
        <div className={styles.bgDecoration1}></div>
        <div className={styles.bgDecoration2}></div>
        <div className={styles.bgDecoration3}></div>
      </div>
    </div>
  )
}

export default PWAInstallPrompt

